import React, { Component, ErrorInfo, ReactNode } from 'react';
import BugCatch, { bugCatchEnabled } from './bugcatch';
import {
  isChunkLoadError,
  markChunkReloadAttempt,
  reloadForNewBuild,
} from './utils/chunkError';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isReloading: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isReloading: false };

  static getDerivedStateFromError(): State {
    return { hasError: true, isReloading: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A chunk that no longer exists on the server means this tab was open
    // across a deploy. Reloading picks up the new build, so it is expected
    // noise rather than something worth reporting.
    if (isChunkLoadError(error) && markChunkReloadAttempt()) {
      this.setState({ isReloading: true });
      reloadForNewBuild();
      return;
    }

    if (bugCatchEnabled) {
      BugCatch.captureException(error, {
        componentStack: errorInfo.componentStack,
      });
    }
  }

  render() {
    if (this.state.isReloading) {
      return (
          <div className="d-flex justify-content-center align-items-center flex-column vh-100">
            <h2>A new version is available.</h2>
            <p className="mt-2">Updating…</p>
          </div>
      );
    }

    if (this.state.hasError) {
      return (
          <div className="d-flex justify-content-center align-items-center flex-column vh-100">
            <h2>Something went wrong.</h2>
            <button
                className="btn btn-primary btn-lg mt-3"
                onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
      );
    }

    return this.props.children;
  }
}
